/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useMemo } from 'react';
import { Breadcrumbs, Fab, makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import { useParams, Link } from 'react-router-dom';

import { RoutePath } from 'shared/enums/RoutePath';
import { useLinkStyles } from 'components/common/useLinkStyles';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import { isEmpty } from 'lodash';
import WelcomeSection from '../../components/welcomeSection/WelcomeSection';
import useOrganization from '../../organization/useOrganization';
import useEditUserProfile from '../../redux/features/user/edit/useEditUserProfile';
import UserInfoDrawer from './components/userDrawer/UserInfoDrawer';
import UserEditor from './components/userProfileAllSections/UserEditor';
import UserOrganizationDetails from './components/userProfileAllSections/UserOrganizationDetails';
// import UserDeleteAccount from './components/userProfileAllSections/UserDeleteAccount';
import UserManageAccount from './components/userProfileAllSections/UserManageAccount';

const useStyles = makeStyles(theme => ({
  toolbar: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
  },
  fab: {
    margin: 0,
    top: 'auto',
    right: 20,
    bottom: 20,
    left: 'auto',
    position: 'fixed',
    backgroundColor: theme.palette[theme.fabColor].main,
    color: theme.palette[theme.fabColor].contrastText,
    '&[data-focustype="key"]': {
      outline: 'none',
      boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
    },
    '&:focus, &:active': {
      backgroundColor: theme.palette[theme.fabColor].active,
      outline: 'none',
    },
    '&:hover': {
      backgroundColor: theme.palette[theme.fabColor].hover,
    },
  },
}));

function UserProfile() {
  const location = useLocation();
  const { t } = useTranslation();
  document.title = t('user-management.my.profile');
  const classes = useStyles();
  const linkClass = useLinkStyles();
  const { userId } = useParams();
  const currentOrganization = useOrganization();

  const editRoles = useMemo(() => ['roles', 'view'].some(word => location.pathname.includes(word)), [location]);
  const showActionButton = useMemo(() => ['roles', 'view'].some(word => location.pathname.includes(word)), [location]);
  const isLargeOrBigger = useMediaQuery(theme => theme.breakpoints.up('lg'));

  const {
    uiSchema,
    validationSchema,
    onUpdateUser,
    onSubmitEditUser,
    user,
    isLoading,
    isValid,
    checkIfFormIsValid,
    isEditMode,
    handleClick,
    openDialog,
    userRoles,
    onSubmit,
    onRoleChange,
    onUpdateUserRoles,
    editRole,
    handleEditRoles,
    disabled,
    toggleDrawer,
    isDrawerOpen,
    isEditMadeByTheSameUser,
  } = useEditUserProfile(userId);

  const showForm = !isEmpty(validationSchema) && !isEmpty(uiSchema) && !isLoading;

  const mainUserName = `${user?.firstName} ${user?.lastName}`;
  const initials = user?.initials ?? `${user?.firstName?.charAt(0)}${user?.lastName?.charAt(0)}`?.toUpperCase();

  return (
    <>
      <div className={classes.content}>
        <Breadcrumbs aria-label="breadcrumb" className="breadcrumbs">
          <Link className={linkClass.link} to={RoutePath.DASHBOARD.path}>
            {t('front.office.dashboard')}
          </Link>
          <Typography color="textPrimary">{t('user-management.my.profile')}</Typography>
        </Breadcrumbs>
        <WelcomeSection
          title={t('user-management.my.profile')}
          description={t('user.management-myProfile.description')}
          contrast="true"
        />
        {user && (
          <div>
            <UserEditor
              isValid={isValid}
              uiSchema={uiSchema}
              jsonSchema={validationSchema}
              editRoles={editRoles}
              isEditAllowed={isEditMadeByTheSameUser}
              editMode={isEditMode}
              showForm={showForm}
              user={user}
              updateUser={onUpdateUser}
              checkIfFormIsValid={checkIfFormIsValid}
              onClick={handleClick}
              onSubmit={onSubmitEditUser}
              onSave={onSubmitEditUser}
            />
            <UserOrganizationDetails
              editRoles={editRoles}
              userRoles={userRoles}
              editRole={editRole}
              showActionButton={showActionButton}
              onSubmit={onSubmit}
              onRoleChange={onRoleChange}
              onUpdateUserRoles={onUpdateUserRoles}
              handleEditRoles={handleEditRoles}
              disabled={disabled}
              onClick={openDialog}
              userId={userId}
              currentOrganization={currentOrganization}
            />
            {!editRoles && isEditMadeByTheSameUser && <UserManageAccount />}
          </div>
        )}
      </div>
      {!isLargeOrBigger && (
        <Fab size="medium" className={classes.fab} onClick={toggleDrawer} aria-label={t('aria.drawer.open')}>
          <MenuIcon />
        </Fab>
      )}
      <UserInfoDrawer
        initials={initials}
        fullName={mainUserName}
        isDrawerOpen={isDrawerOpen}
        toggleDrawer={toggleDrawer}
      />
    </>
  );
}

export default UserProfile;
